<template>
  <div
    class="relative flex flex-auto flex-col gap-3 font-ds pb-4"
    role="status"
    aria-busy="true"
    :aria-label="message"
  >
    <div
      class="flex-auto grid gap-4 overflow-hidden"
      :class="showPanel ? 'grid-cols-[17rem_minmax(0,1fr)]' : 'grid-cols-1'"
    >
      <aside
        v-if="showPanel"
        class="flex flex-none flex-col gap-5 self-start rounded-ds-lg border border-border bg-card p-4 pb-5 shadow-ds-sm"
      >
        <div
          v-for="section in FIELD_SECTIONS"
          :key="section.caption"
          class="flex flex-col gap-2"
        >
          <div class="flex items-center justify-between">
            <DSSkeleton :class="skeletonClass('h-3 w-14')" />
            <DSSkeleton :class="skeletonClass('h-3 w-8')" />
          </div>
          <div class="flex flex-col gap-0.5">
            <div
              v-for="width in section.rows"
              :key="width"
              class="flex items-center gap-2 px-2 py-1.5 -mx-2"
            >
              <DSSkeleton :class="skeletonClass('size-4 shrink-0 rounded-ds-sm')" />
              <DSSkeleton
                :class="skeletonClass('h-3.5')"
                :style="{ width: `${width}%` }"
              />
            </div>
          </div>
        </div>
      </aside>

      <div class="flex flex-col gap-3 overflow-hidden">
        <div
          v-if="!hidePanel"
          class="flex flex-wrap items-center gap-1.5"
        >
          <DSButton
            v-if="!isMobile"
            variant="secondary"
            size="icon-sm"
            disabled
            tabindex="-1"
            aria-hidden="true"
          >
            <DSSkeleton
              :class="skeletonClass('size-3.5')"
            />
          </DSButton>
          <PivotPanelSeparator v-if="!isMobile" />
          <DSButton
            variant="secondary"
            :size="isMobile ? 'icon-sm' : 'sm'"
            disabled
            tabindex="-1"
            aria-hidden="true"
          >
            <DSSkeleton
              v-if="!isMobile"
              :class="skeletonClass('h-3.5 w-12')"
            />
          </DSButton>
          <DSButton
            v-for="sortLabel in SORT_LABEL_WIDTHS"
            :key="sortLabel"
            variant="secondary"
            :size="isMobile ? 'icon-sm' : 'sm'"
            disabled
            tabindex="-1"
            aria-hidden="true"
          >
            <DSSkeleton
              v-if="!isMobile"
              :class="skeletonClass('h-3.5')"
              :style="{ width: `${sortLabel}px` }"
            />
          </DSButton>
          <PivotPanelSeparator v-if="!isMobile" />
          <DSButton
            variant="ghost"
            size="sm"
            :class="isMobile ? 'px-2' : ''"
            disabled
            tabindex="-1"
            aria-hidden="true"
          >
            <DSSkeleton :class="skeletonClass('h-3.5 w-16')" />
          </DSButton>
          <div class="ml-auto flex items-center gap-1.5">
            <DSButton
              variant="secondary"
              size="icon-sm"
              disabled
              tabindex="-1"
              aria-hidden="true"
            >
              <DSSkeleton
                :class="skeletonClass('size-3.5')"
              />
            </DSButton>
            <DSButton
              variant="secondary"
              size="icon-sm"
              disabled
              tabindex="-1"
              aria-hidden="true"
            >
              <DSSkeleton
                :class="skeletonClass('size-3.5')"
              />
            </DSButton>
          </div>
        </div>

        <div class="relative flex flex-auto flex-col overflow-hidden rounded-ds-lg border border-border bg-card shadow-ds-sm">
          <div
            class="grid"
            :style="gridStyle"
          >
            <div
              v-for="(width, col) in HEADER_WIDTHS"
              :key="col"
              class="border-r border-b border-border px-4 py-2.75 last:border-r-0"
            >
              <DSSkeleton
                :class="skeletonClass('h-3.5')"
                :style="{ width: `${width}%` }"
              />
            </div>
          </div>
          <div
            v-for="row in ROWS"
            :key="row"
            class="grid"
            :style="gridStyle"
          >
            <div
              v-for="(width, col) in rowWidths(row)"
              :key="col"
              class="border-r border-b border-border/30 px-4 py-2.75 last:border-r-0"
            >
              <DSSkeleton
                :class="skeletonClass('h-3.5')"
                :style="{ width: `${width}%`, marginLeft: col === 0 ? undefined : 'auto' }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <span class="sr-only">{{ message }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { DSButton, DSSkeleton } from '@ds'

import PivotPanelSeparator from './primitives/PivotPanelSeparator.vue'

type Props = {
  /** Announced to assistive tech only — there is no visible loading copy, unlike `UISkeletonPivot`. */
  message?: string
  /** Freezes the shimmer — for static previews (widget editors), not live loading states. */
  static?: boolean
  /** Hides the fields panel, toolbar and tabs row, leaving only the grid — mirrors a compact editor preview. */
  hidePanel?: boolean
  isMobile?: boolean
}

const {
  message = 'Pivot is loading...',
  static: isStatic,
  hidePanel,
  isMobile,
} = defineProps<Props>()

defineOptions({
  name: 'DSPivotSkeleton',
})

// Caption + "n of n" row, then a handful of checkbox/label rows — matches `PivotFieldSection`.
const FIELD_SECTIONS = [
  { caption: 'Columns', rows: [55, 40] },
  { caption: 'Rows', rows: [65, 45, 35] },
  { caption: 'Values', rows: [50] },
]

const SORT_LABEL_WIDTHS = [52, 36]
const HEADER_WIDTHS = [22, 16, 16, 16, 16, 14]
const ROWS = 7

const showPanel = computed(() => !isMobile && !hidePanel)

const gridStyle = computed(() => ({
  gridTemplateColumns: `minmax(10rem, 1.5fr) repeat(${HEADER_WIDTHS.length - 1}, minmax(5rem, 1fr))`,
}))

// Deterministic, slightly irregular widths so rows don't look like a single repeated tile.
const rowWidths = (row: number) =>
  HEADER_WIDTHS.map((_, col) => (col === 0 ? 70 - ((row * 7) % 25) : 55 - ((row + col) % 20)))

const skeletonClass = (base: string) => isStatic ? `${base} animate-none` : base
</script>
